import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {type ReactNode, useCallback, useEffect, useState} from 'react';

import {
  Action,
  css,
  Drawer,
  EmptyState,
  Icon,
  List,
  ListItem,
  Tooltip,
  view,
} from '@sail/ui';
import {AnimatePresence} from '@sail/engine';
import {
  arrowLeft,
  call,
  cancel,
  chat,
  history,
  spinner,
} from '@sail/icons/react/Icon';

import type {Conversation} from '@stripe-internal/conversations-client';
import {useAnalytics} from '@stripe-internal/conversations-ui';
import {ViewMode} from '@stripe-internal/support-conversations-controller';

import {LoadingScreen} from '../../../core/components/LoadingScreen';
import {
  useThreadsConversationViewModelState,
  useThreadsDashboardUrl,
  useThreadsHistoryViewModel,
  useThreadsHistoryViewModelState,
  useThreadsViewMode,
} from '../../app/hooks';

const messages = defineMessages({
  title: {
    id: 'conversations_assistant.threads.history.title',
    description: 'Title for the recent conversations drawer',
    defaultMessage: 'Recent chats',
  },
  back: {
    id: 'conversations_assistant.threads.history.back',
    description: 'Label for returning from history to the conversation',
    defaultMessage: 'Back',
  },
  newAssistantChat: {
    id: 'conversations_assistant.threads.history.newAssistantChat',
    description: 'Default title for a conversation history row',
    defaultMessage: 'New assistant chat',
  },
  updatedAt: {
    id: 'conversations_assistant.threads.history.updatedAt',
    description: 'Timestamp label for a conversation history row',
    defaultMessage: '{date} · {time}',
  },
  activeStatus: {
    id: 'conversations_assistant.threads.history.activeStatus',
    description: 'Status for a conversation with active bot or agent',
    defaultMessage: '{activeStatus}',
  },
  assistantThinking: {
    id: 'conversations_assistant.threads.history.assistantThinking',
    description: 'Status for a conversation when assistants thinking',
    defaultMessage: 'Thinking...',
  },
  chattingWithSupport: {
    id: 'conversations_assistant.threads.history.chattingWithSupport',
    description: 'Status for a conversation with an active live chat',
    defaultMessage: 'Chatting with support',
  },
  chattingWithAgent: {
    id: 'conversations_assistant.threads.history.chattingWithAgent',
    description:
      'Status for a conversation with an active live chat with a named agent',
    defaultMessage: 'Chatting with <name>{agentName}</name>',
  },
  callingWithSupport: {
    id: 'conversations_assistant.threads.history.callingWithSupport',
    description: 'Status for a conversation with a callback request',
    defaultMessage: 'Calling with support',
  },
  viewSupportCases: {
    id: 'conversations_assistant.threads.history.viewSupportCases',
    description: 'External link into Dashboard support cases page',
    defaultMessage: 'View support cases',
  },
  emptyTitle: {
    id: 'conversations_assistant.threads.history.emptyTitle',
    description: 'Title for empty recent conversations',
    defaultMessage: 'No recent conversations',
  },
  emptyDescription: {
    id: 'conversations_assistant.threads.history.emptyDescription',
    description: 'Description for empty recent conversations',
    defaultMessage: 'Conversations will appear here after they are created.',
  },
  errorTitle: {
    id: 'conversations_assistant.threads.history.errorTitle',
    description: 'Title when recent conversations could not load',
    defaultMessage: 'Unable to load recent conversations',
  },
  retry: {
    id: 'conversations_assistant.threads.history.retry',
    description: 'Label for retrying recent conversation loading',
    defaultMessage: 'Retry',
  },
});

const inlineRowStyles = css({
  stack: 'x',
  alignY: 'center',
  gap: 'xsmall',
});

const truncatedNameStyles = css({
  display: 'inline-block',
  maxWidth: '7ch',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
});

const iconButtonStyles = css({
  display: 'inline-flex',
  borderRadius: 'rounded',
  padding: 'small',
  ':hover': {
    backgroundColor: 'offset',
  },
});

function ThreadsHistoryDrawerHeader({splitPane}: {splitPane: boolean}) {
  const intl = useIntl();
  const historyViewModel = useThreadsHistoryViewModel();
  const analytics = useAnalytics();
  const handleBackClick = useCallback(() => {
    analytics.action('stripe_assistant_history_back_click');
    historyViewModel.back();
  }, [analytics, historyViewModel]);

  return (
    <view.div
      data-testid="threads-history-drawer-header"
      css={{
        stack: 'x',
        alignY: 'center',
        alignX: 'start',
        gap: 'xsmall',
        paddingX: 'medium',
        paddingY: 'small',
        minHeight: '52px',
        borderBottomStyle: 'solid',
        borderBottomWidth: 'small',
        borderColor: 'border',
        distribute: splitPane ? 'space-between' : undefined,
      }}
    >
      {!splitPane ? (
        <Tooltip
          type="label"
          trigger={
            <Action
              aria-label={intl.formatMessage(messages.back)}
              uses={[iconButtonStyles]}
              css={{bleedLeft: 'small'}}
              onClick={handleBackClick}
            >
              <Icon icon={arrowLeft} size="xsmall" />
            </Action>
          }
        >
          {intl.formatMessage(messages.back)}
        </Tooltip>
      ) : null}
      <view.h1
        css={{
          margin: 'space.0',
          font: 'heading.small',
          textAlign: 'left',
        }}
      >
        {intl.formatMessage(messages.title)}
      </view.h1>
      {splitPane ? (
        <Tooltip
          type="label"
          trigger={
            <Action
              aria-label={intl.formatMessage(messages.back)}
              uses={[iconButtonStyles]}
              onClick={handleBackClick}
            >
              <Icon icon={cancel} size="xsmall" />
            </Action>
          }
        >
          {intl.formatMessage(messages.back)}
        </Tooltip>
      ) : null}
    </view.div>
  );
}

function ThreadsHistoryErrorState() {
  const intl = useIntl();
  const historyViewModel = useThreadsHistoryViewModel();
  const analytics = useAnalytics();
  const handleRetryClick = useCallback(() => {
    analytics.action('stripe_assistant_history_retry_click');
    historyViewModel.load();
  }, [analytics, historyViewModel]);

  return (
    <EmptyState
      icon={history}
      size="medium"
      title={intl.formatMessage(messages.errorTitle)}
      actions={
        <Action uses={[iconButtonStyles]} onClick={handleRetryClick}>
          {intl.formatMessage(messages.retry)}
        </Action>
      }
    />
  );
}

function ThreadsHistoryEmptyState() {
  const intl = useIntl();

  return (
    <EmptyState
      icon={history}
      size="medium"
      title={intl.formatMessage(messages.emptyTitle)}
      description={intl.formatMessage(messages.emptyDescription)}
    />
  );
}

function getActiveAgentNames(conversation: Conversation | null): string[] {
  if (!conversation) return [];
  return Object.values(conversation.participants)
    .filter((p) => p.role === 'agent')
    .map((p) => p.displayName)
    .filter((name): name is string => !!name);
}

// Participants (and thus agent names) can change mid-conversation, e.g. when
// a chat is transferred to a different agent. `conversation.participants` is
// only a snapshot, so subscribe to participantsObservable() to stay current.
function useActiveAgentNames(conversation: Conversation | null): string[] {
  const [agentNames, setAgentNames] = useState<string[]>(() =>
    getActiveAgentNames(conversation),
  );

  useEffect(() => {
    setAgentNames(getActiveAgentNames(conversation));
    if (!conversation) return undefined;

    const sub = conversation
      .participantsObservable()
      .subscribe(() => setAgentNames(getActiveAgentNames(conversation)));
    return () => sub.unsubscribe();
  }, [conversation]);

  return agentNames;
}

function ThreadsHistoryList() {
  const intl = useIntl();
  const historyViewModel = useThreadsHistoryViewModel();
  const analytics = useAnalytics();
  const {currentConversationId, overviews} = useThreadsHistoryViewModelState();
  const {conversation} = useThreadsConversationViewModelState();
  const agentNames = useActiveAgentNames(conversation);

  return (
    <List
      aria-label={intl.formatMessage(messages.title)}
      onAction={(id) => {
        analytics.action('stripe_assistant_history_thread_click');
        historyViewModel.selectConversation(String(id));
      }}
      css={{paddingX: 'medium', paddingTop: 'small'}}
    >
      {overviews.map((overview) => {
        const timestamp = overview.last_updated_at * 1000;
        const updatedAtDate = intl.formatDate(timestamp, {
          month: 'short',
          day: 'numeric',
        });
        const updatedAtTime = intl.formatTime(timestamp, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        const updatedAt = intl.formatMessage(messages.updatedAt, {
          date: updatedAtDate,
          time: updatedAtTime,
        });

        let activeStatus: ReactNode = null;
        if (overview?.assistant?.status === 'in_progress') {
          activeStatus = (
            <view.span uses={[inlineRowStyles]}>
              <Icon icon={spinner} size="xsmall" />
              {intl.formatMessage(messages.assistantThinking)}
            </view.span>
          );
        } else if (overview?.live_chat?.status === 'active') {
          const names = overview.id === currentConversationId ? agentNames : [];
          const agentName = names.join(', ');
          activeStatus = (
            <view.span uses={[inlineRowStyles]} css={{color: 'action.primary'}}>
              <Icon icon={chat} size="xsmall" />
              {agentName ? (
                <FormattedMessage
                  {...messages.chattingWithAgent}
                  values={{
                    agentName,
                    name: (chunks: ReactNode) => (
                      <Tooltip
                        type="label"
                        trigger={
                          <view.span uses={[truncatedNameStyles]}>
                            {chunks}
                          </view.span>
                        }
                      >
                        {agentName}
                      </Tooltip>
                    ),
                  }}
                />
              ) : (
                intl.formatMessage(messages.chattingWithSupport)
              )}
            </view.span>
          );
        } else if (overview?.request_a_call?.status === 'queued') {
          activeStatus = (
            <view.span uses={[inlineRowStyles]} css={{color: 'action.primary'}}>
              <Icon icon={call} size="xsmall" />
              {intl.formatMessage(messages.callingWithSupport)}
            </view.span>
          );
        }

        return (
          <ListItem
            key={overview.id}
            id={overview.id}
            data-testid={`threads-history-row-${overview.id}`}
            css={{
              borderTopStyle: 'none',
              ':last-child': {borderBottomStyle: 'none'},
            }}
            title={
              overview.title || intl.formatMessage(messages.newAssistantChat)
            }
            description={
              activeStatus ? (
                <view.span uses={[inlineRowStyles]}>
                  {activeStatus} | {updatedAt}
                </view.span>
              ) : (
                updatedAt
              )
            }
          />
        );
      })}
    </List>
  );
}

function ThreadsHistoryDrawerContent() {
  const {error, loading, overviews} = useThreadsHistoryViewModelState();

  if (loading && overviews.length === 0) {
    return <LoadingScreen />;
  }

  if (error && !loading) {
    return <ThreadsHistoryErrorState />;
  }

  if (overviews.length === 0) {
    return <ThreadsHistoryEmptyState />;
  }

  return <ThreadsHistoryList />;
}

function ThreadsHistoryDrawerFooter() {
  const intl = useIntl();
  const dashboardUrl = useThreadsDashboardUrl();
  const analytics = useAnalytics();
  const handleViewSupportCasesClick = useCallback(() => {
    analytics.action('stripe_assistant_history_view_support_cases_click');
  }, [analytics]);

  return (
    <view.div
      css={{
        paddingX: 'medium',
        paddingY: 'medium',
        borderTopStyle: 'solid',
        borderTopWidth: 'small',
        borderColor: 'border',
      }}
    >
      <Action
        href={`${dashboardUrl}/support`}
        target="_blank"
        uses={[inlineRowStyles]}
        css={{color: 'action.primary'}}
        onClick={handleViewSupportCasesClick}
      >
        {intl.formatMessage(messages.viewSupportCases)}
      </Action>
    </view.div>
  );
}

function ThreadsHistoryDrawerSurface({
  fillWidth,
  splitPane,
}: {
  fillWidth?: boolean;
  splitPane: boolean;
}) {
  const intl = useIntl();
  const historyViewModel = useThreadsHistoryViewModel();
  const handleClose = useCallback(
    () => historyViewModel.close(),
    [historyViewModel],
  );
  const viewMode = useThreadsViewMode();
  const isSupportWidget = viewMode === ViewMode.SupportWidget;
  const shouldFillWidth = fillWidth ?? isSupportWidget;

  return (
    <Drawer
      blocking={false}
      direction="right"
      bottomSheetVariant={splitPane ? 'fullscreen-sheet' : undefined}
      aria-label={intl.formatMessage(messages.title)}
      data-testid="threads-history-drawer"
      header={<ThreadsHistoryDrawerHeader splitPane={splitPane} />}
      footer={<ThreadsHistoryDrawerFooter />}
      onClose={handleClose}
      uses={[
        css({
          borderLeftWidth: 0,
          borderTopWidth: 0,
          borderRadius: 0,
          boxShadow: 'none',
          top: 'var(--sail-layer-margin-top, 0px)',
        }),
        splitPane
          ? css({
              backgroundColor: 'transparent',
              borderLeftWidth: 'small',
            })
          : null,
        shouldFillWidth ? css({width: 'fill'}) : null,
      ]}
      subviews={
        shouldFillWidth
          ? {
              content: {css: {width: 'fill'}},
              header: {css: {width: 'fill'}},
            }
          : undefined
      }
    >
      <ThreadsHistoryDrawerContent />
    </Drawer>
  );
}

export function ThreadsHistoryDrawer({
  fillWidth,
  splitPane = false,
}: {
  fillWidth?: boolean;
  splitPane?: boolean;
}) {
  const {isOpen} = useThreadsHistoryViewModelState();

  return (
    <AnimatePresence>
      {isOpen ? (
        <ThreadsHistoryDrawerSurface
          fillWidth={fillWidth}
          splitPane={splitPane}
        />
      ) : null}
    </AnimatePresence>
  );
}
